import React, { useEffect, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLPDetails, unSet } from '../../slices/createLPSlice';
import { setEdit } from '../../slices/authSlice';
import { LandingPageServe } from '../../services/api/LandingPageServ';
import FileUploader from '../../components/FileUploader';
import EmbeddedFileExplorer from '../../components/EmbeddedFileExplorer';

const DOMAINS = [
  { label: 'knowledgeboats.com', value: 'knowledgeboats-com' },
  { label: 'knowledgeboats.info', value: 'knowledgeboats-info' },
  { label: 'hitech-articles.com', value: 'hitech-articles' },
];

const EditExternalLP = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useRef(null);
  const lpDetails = useSelector((state) => state.createLp.data);
  const [activeTab, setActiveTab] = useState(0);
  const [viewUploader, setViewUploader] = useState(false);
  const [saving, setSaving] = useState(false);

  const { control, formState: { errors }, handleSubmit, setValue, reset } = useForm();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await LandingPageServe.single(id);
        dispatch(setLPDetails(data));
        console.log('LP Details:', data);
        reset({
          name: data.name || '',
          campaignId: data.campaignId || '',
          campaignName: data.campaignName || '',
          description: data.description || '',
          client: data.client || '',
          thumbnail: data.thumbnail || '',
          domain: data.hostingDetails?.path || 'knowledgeboats-com',
        });
      } catch (e) {
        console.log(e);
      }
    };
    load();
    dispatch(setEdit(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getFormErrorMessage = (name) =>
    errors[name] ? <small className="p-error">{errors[name].message}</small> : <small className="p-error">&nbsp;</small>;

  const onSave = async (data) => {
    setSaving(true);
    try {
      const domainLabel = DOMAINS.find(d => d.value === data.domain)?.label || data.domain;
      const payload = {
        ...data,
        hostingDetails: { domain: domainLabel, path: data.domain },
      };
      await LandingPageServe.update(id, payload);
      dispatch(setLPDetails({ ...lpDetails, ...payload }));
      toast.current.show({ severity: 'success', summary: 'Saved', detail: 'Changes saved successfully' });
    } catch (e) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseUploader = (data) => {
    if (data) setValue('thumbnail', data.Location, { shouldValidate: true });
    setViewUploader(false);
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card">
        <div className="flex justify-content-between pb-1">
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#334155' }}>{lpDetails.name}</span>
          <div className="flex gap-2">
            <Button icon="pi pi-times" className="w-2rem h-2rem p-0" severity="warning" rounded outlined tooltip="Back" tooltipOptions={{ position: 'left' }}
              onClick={() => { dispatch(unSet()); navigate(`../view-external-lp/${id}`); }} />
          </div>
        </div>

        <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index) }>
          <TabPanel header="Summary">
            <div className="grid pt-4">
              <div className="col-6 col-offset-3">
                <Fieldset legend="Edit External LP">
                  <form>
                    <div className="field">
                      <Controller name="name" control={control} rules={{ required: 'Name is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} className={classNames({ 'p-invalid': fieldState.error })} onChange={e => field.onChange(e.target.value)} style={{ width: '100%' }} />
                              <label>Name</label>
                            </span>
                            {getFormErrorMessage(field.name)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field">
                      <Controller name="campaignId" control={control} rules={{ required: 'Campaign ID is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} className={classNames({ 'p-invalid': fieldState.error })} onChange={e => field.onChange(e.target.value)} style={{ width: '100%' }} />
                              <label>Campaign ID</label>
                            </span>
                            {getFormErrorMessage(field.name)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field">
                      <Controller name="campaignName" control={control}
                        render={({ field }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} onChange={e => field.onChange(e.target.value)} style={{ width: '100%' }} />
                              <label>Campaign Name</label>
                            </span>
                            <small className="p-error">&nbsp;</small>
                          </>
                        )}
                      />
                    </div>
                    <div className="field">
                      <Controller name="description" control={control} rules={{ required: 'Description is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} className={classNames({ 'p-invalid': fieldState.error })} onChange={e => field.onChange(e.target.value)} style={{ width: '100%' }} />
                              <label>Description</label>
                            </span>
                            {getFormErrorMessage(field.name)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field">
                      <Controller name="client" control={control} rules={{ required: 'Client is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} className={classNames({ 'p-invalid': fieldState.error })} onChange={e => field.onChange(e.target.value)} style={{ width: '100%' }} />
                              <label>Client</label>
                            </span>
                            {getFormErrorMessage(field.name)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field">
                      <Controller name="domain" control={control} rules={{ required: 'Domain is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <label style={{ fontWeight: 600, marginBottom: '0.4rem', display: 'block', color: '#64748b', fontSize: '0.9rem' }}>Domain</label>
                            <Dropdown value={field.value} options={DOMAINS} onChange={e => field.onChange(e.value)}
                              placeholder="Select Domain" className={classNames({ 'p-invalid': fieldState.error })} style={{ width: '100%' }} />
                            {getFormErrorMessage(field.name)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field p-inputgroup pt-3">
                      <Controller name="thumbnail" control={control} rules={{ required: 'Thumbnail is required.' }}
                        render={({ field, fieldState }) => (
                          <>
                            <span className="p-float-label">
                              <InputText id={field.name} value={field.value || ''} style={{ borderRadius: '5px' }} className={classNames({ 'p-invalid': fieldState.error })} onChange={e => field.onChange(e.target.value)} />
                              <label>Thumbnail</label>
                            </span>
                          </>
                        )}
                      />
                      <Button label="Upload" icon="pi pi-cloud-upload" onClick={e => { e.preventDefault(); setViewUploader(true); }} />
                    </div>
                    {getFormErrorMessage('thumbnail')}
                  </form>
                  <div className="flex justify-content-end mt-3">
                    <Button label="Save" icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} disabled={saving} severity="success" onClick={handleSubmit(onSave)} />
                  </div>
                </Fieldset>
                <FileUploader open={viewUploader} handleCloseUploader={handleCloseUploader} />
              </div>
            </div>
          </TabPanel>
          <TabPanel header="Files">
            {lpDetails.hostingDetails?.path && lpDetails.campaignId && (
              <EmbeddedFileExplorer
                domain={lpDetails.hostingDetails.path}
                domainLabel={lpDetails.hostingDetails.domain}
                campaignId={lpDetails.campaignId}
                pocId={lpDetails._id}

              />
            )}
          </TabPanel>
        </TabView>
      </div>
    </>
  );
};

export default EditExternalLP;
